/**
 * Where to resume a series — shared by the app and the web app (`@shared`).
 *
 * The resume point is the episode right after the FURTHEST episode the user
 * has watched, not the first unwatched one: someone who skipped the early
 * seasons of a show and is following the current one continues there, and a
 * series whose latest episode is watched is caught up even with gaps behind
 * it. Specials (season 0) only count when a series has nothing else.
 *
 * Pure: season summaries carry per-season `watchedEpisodes`; the exact
 * episode needs that season's episode list (`resumeEpisode`). Labels that
 * only have summaries use `resumeFromSummaries` (an estimate within the
 * resume season).
 */

export interface SeasonProgressLike {
  seasonNumber: number;
  episodeCount: number;
  watchedEpisodes?: number | null;
}

export interface EpisodeLike {
  episodeNumber: number;
  watched?: boolean;
}

export interface EpisodeRef {
  season: number;
  episode: number;
}

function ordered<S extends SeasonProgressLike>(seasons: readonly S[]): S[] {
  const withEpisodes = seasons
    .filter((s) => s.episodeCount > 0)
    .sort((a, b) => a.seasonNumber - b.seasonNumber);
  const regular = withEpisodes.filter((s) => s.seasonNumber > 0);
  return regular.length > 0 ? regular : withEpisodes;
}

/**
 * The season to resume in: the last season with anything watched while it
 * still has unwatched episodes, else the season after it; the first season
 * when nothing is watched. Null when caught up (or no episode data).
 */
export function resumeSeason<S extends SeasonProgressLike>(seasons: readonly S[]): S | null {
  const list = ordered(seasons);
  let last = -1;
  list.forEach((s, i) => {
    if ((s.watchedEpisodes ?? 0) > 0) last = i;
  });
  if (last === -1) return list[0] ?? null;
  const season = list[last];
  if ((season.watchedEpisodes ?? 0) < season.episodeCount) return season;
  return list[last + 1] ?? null;
}

/**
 * The exact resume episode, given the resume season's episode list: the
 * first unwatched episode after the furthest watched one; past the end of
 * the season, episode 1 of the next season with episodes; null when nothing
 * follows. Without episode rows (still ingesting) it falls back to the
 * sequential guess from the summary.
 */
export function resumeEpisode(
  seasons: readonly SeasonProgressLike[],
  seasonNumber: number,
  episodes: readonly EpisodeLike[],
): EpisodeRef | null {
  if (episodes.length === 0) {
    const summary = seasons.find((s) => s.seasonNumber === seasonNumber);
    return { season: seasonNumber, episode: Math.max(1, (summary?.watchedEpisodes ?? 0) + 1) };
  }
  const sorted = [...episodes].sort((a, b) => a.episodeNumber - b.episodeNumber);
  const furthest = sorted.reduce((max, e) => (e.watched ? Math.max(max, e.episodeNumber) : max), 0);
  const next = sorted.find((e) => !e.watched && e.episodeNumber > furthest);
  if (next) return { season: seasonNumber, episode: next.episodeNumber };
  const following = ordered(seasons).find((s) => s.seasonNumber > seasonNumber);
  return following ? { season: following.seasonNumber, episode: 1 } : null;
}

/** Summary-only estimate: the resume season at `watched + 1`. */
export function resumeFromSummaries(seasons: readonly SeasonProgressLike[]): EpisodeRef | null {
  const season = resumeSeason(seasons);
  if (!season) return null;
  return {
    season: season.seasonNumber,
    episode: Math.min(season.episodeCount, (season.watchedEpisodes ?? 0) + 1),
  };
}

/**
 * NEW tag: a recent airing the user has not reached yet — at or after the
 * resume point, or (when caught up) beyond the last episode the catalog
 * knows about. Gaps in earlier seasons never make an airing "new".
 */
export function airingIsNew(
  airing: EpisodeRef,
  resume: EpisodeRef | null,
  seasons: readonly SeasonProgressLike[],
): boolean {
  if (resume) {
    return airing.season > resume.season || (airing.season === resume.season && airing.episode >= resume.episode);
  }
  const list = ordered(seasons);
  const last = list[list.length - 1];
  if (!last) return false;
  return airing.season > last.seasonNumber || (airing.season === last.seasonNumber && airing.episode > last.episodeCount);
}
